import { Alert, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CyProfile } from "./CyProfile";
import { ContainerWithHeaderAndMenu } from "../menuandheader/ContainerWithHeaderAndMenu";
import { useTranslation } from "react-i18next";
import { useProfileQuery } from "./useProfileQuery";
import { RoutePaths } from "../RoutePaths";
import Auth from "../auth/Auth";

interface Props {
  auth?: Auth;
}

export default function ViewProfile(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();

  const targetId = paramId ? decodeURIComponent(paramId).trim() : "";
  const currentSubject = props.auth?.isAuthenticated()
    ? props.auth.getAuth0Subject()
    : undefined;

  const isOwnProfile = Boolean(
    !targetId || (currentSubject && targetId === currentSubject),
  );

  const { profile, fetching, error } = useProfileQuery(targetId, isOwnProfile);

  if (isOwnProfile) {
    return <Navigate to={RoutePaths.toProfile()} replace />;
  }

  const hasProfileData = Boolean(
    profile?.firstName?.trim() || profile?.lastName?.trim(),
  );

  return (
    <ContainerWithHeaderAndMenu icon={faUser} title={t("profile.userProfile")}>
      {fetching ? (
        <p>
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
          {t("common.loadingText")}
        </p>
      ) : error ? (
        <Alert
          color="warning"
          fade={false}
          data-cy={CyProfile.ProfileNotAvailableAlert}
          data-testid={CyProfile.ProfileNotAvailableAlert}
        >
          {t("profile.profileNotAvailable")}
        </Alert>
      ) : hasProfileData ? (
        <div className="mb-4">
          <h5>
            <FontAwesomeIcon icon={faUser} className="me-1" />
            {t("profile.personalInfo")}
          </h5>
          <div className="mb-2">
            <strong>{t("profile.firstName")}:</strong>{" "}
            <span
              data-cy={CyProfile.ReadOnlyFirstName}
              data-testid={CyProfile.ReadOnlyFirstName}
            >
              {profile?.firstName || "-"}
            </span>
          </div>
          <div className="mb-3">
            <strong>{t("profile.lastName")}:</strong>{" "}
            <span
              data-cy={CyProfile.ReadOnlyLastName}
              data-testid={CyProfile.ReadOnlyLastName}
            >
              {profile?.lastName || "-"}
            </span>
          </div>
        </div>
      ) : (
        <Alert
          color="info"
          fade={false}
          data-cy={CyProfile.NoProfileDataAlert}
          data-testid={CyProfile.NoProfileDataAlert}
        >
          {t("profile.noProfileData")}
        </Alert>
      )}

      <Button
        color="secondary"
        onClick={() => navigate(RoutePaths.toProfile())}
        data-cy={CyProfile.BackToOwnProfileButton}
        data-testid={CyProfile.BackToOwnProfileButton}
        className="mt-3"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
        {t("profile.backToOwnProfile")}
      </Button>
    </ContainerWithHeaderAndMenu>
  );
}
